import { invariant } from '../util/invariant';
import type { BlobView } from '../io/blobView';

/*
  The formal specification for the file format that we generally refer to as
  MP4 or MOV is ISO BMFF.

  ISO BMFF Byte Stream Format
  ISO/IEC 14496-12:2015
  https://b.goeswhere.com/ISO_IEC_14496-12_2015.pdf
  https://standards.iso.org/ittf/PubliclyAvailableStandards/c068960_ISO_IEC_14496-12_2015.zip
  ISO/IEC 14496-12:2005
  https://sce.umkc.edu/faculty-sites/lizhu/teaching/2020.spring.video/ref/mp4.pdf
  https://web.archive.org/web/20201025155850/https://sce.umkc.edu/faculty-sites/lizhu/teaching/2020.spring.video/ref/mp4.pdf
*/

/*
  An ISO BMFF byte stream is orginized as sequence of length prefixed boxes.
*/
export class Box {
  constructor(
    readonly type: string,
    readonly blob: BlobView,
    readonly size: number
  ) {}
}

/*
  Boxes can be nested.
*/
export class BoxTreeBranch {
  constructor(readonly typePath: string[], readonly box: Box) {}
}

/*
  The first 8 bytes (or more) of a box contains its header which both describe
  the size and type of the box.
*/
export async function readBox(blob: BlobView): Promise<Box | null> {

  const boxHeader = blob.slice(0, 8);
  const ab = await boxHeader.arrayBuffer();
  if (ab.byteLength < 8) {
    return null;
  }
  const dw = new DataView(ab);
  const size = dw.getUint32(0, false);
  const decoder = new TextDecoder();
  const type = decoder.decode(ab.slice(4, 8));
  invariant(size > 1, 'unsupported box size');
  return new Box(type, blob.slice(0, size), size);
}

/**
 * Split a blob into a sequence of boxes.
 */
export async function* splitBoxes(blobSequence: BlobView): AsyncGenerator<Box> {
  while (true) {
    const box = await readBox(blobSequence);
    if (box == null) {
      return;
    }
    yield box;
    blobSequence = blobSequence.slice(box.size);
  }
}

async function* noBoxes(): AsyncGenerator<Box> {
  // no-op
}

/**
 * Obtain the boxes contained within a parent box. This only works for boxes of
 * known types, since knowledge about this size of the box header is needed to
 * locate the child boxes.
 */
export function childBoxes(parent: Box): AsyncGenerator<Box> {
  if (parentBoxes.has(parent.type)) {
    return splitBoxes(parent.blob.slice(childBoxOffset));
  }
  return noBoxes();
}

/**
 *These are the boxes we know the how to locate the child boxes for.
 */
const parentBoxes = new Set([
  'moov',
  'moof',
  'mfra',
  'trak',
  'mvex',
  'udta',
  'traf',
  'edts',
  'mdia',
  'minf',
  'dinf',
  'stbl',
]);

const childBoxOffset = 8;

/**
 * Generate a sequence of boxes that by visiting every nested box the order they
 * appear in a blob.
 */
export async function* traverseBoxTree(
  typePath: string[],
  blobSequence: BlobView
): AsyncGenerator<BoxTreeBranch> {
  for await (const branch of traverseBoxForrest(typePath, splitBoxes(blobSequence))) {
    yield branch;
  }
}

export async function* traverseBoxForrest(typePath: string[], siblings: AsyncIterable<Box>) {
  for await (const box of siblings) {
    yield new BoxTreeBranch(typePath, box);
    if (!parentBoxes.has(box.type)) {
      continue;
    }
    const subBlob = box.blob.slice(childBoxOffset);
    const subTypePath = [...typePath, box.type];
    for await (const branch of traverseBoxTree(subTypePath, subBlob)) {
      yield branch;
    }
  }
}

/**
 * A parser for individual fields of a box.
 */
class BoxParser {
  pos = 0;
  constructor(private data: DataView) {}

  u8() {
    return this.data.getUint8(this.pos++);
  }

  u16() {
    // eslint-disable-next-line no-bitwise
    return (this.u8() << 8) | this.u8();
  }

  u24() {
    // eslint-disable-next-line no-bitwise
    return (this.u8() << 16) | (this.u8() << 8) | this.u8();
  }

  s32() {
    // eslint-disable-next-line no-bitwise
    return (this.u8() << 24) | (this.u8() << 16) | (this.u8() << 8) | this.u8();
  }

  u32() {
    // eslint-disable-next-line no-bitwise
    return this.s32() >>> 0;
  }

  u64() {
    // eslint-disable-next-line no-bitwise
    return (BigInt(this.u32()) << BigInt(32)) | BigInt(this.u32());
  }

  hex(byteCount: number) {
    return [...Array(byteCount)]
      .map(() => this.u8().toString(16).padStart(2, '0'))
      .join('');
  }

  ascii(byteCount: number) {
    return [...Array(byteCount)]
      .map(() => String.fromCharCode(this.u8()))
      .join('');
  }
}

/**
 * Read and parse a Track Fragment Header Box.
 *
 * An ISOBMFF is roughly equivalent to an FFmpeg stream.
 */
export async function readTfhd(blob: BlobView) {
  const ab = await blob.arrayBuffer();
  const p = new BoxParser(new DataView(ab));
  const fields = {
    size: p.u32(),
    type: p.ascii(4),
    version: p.u8(),
    flags: p.u24(),
    track_ID: p.u32(),
  };

  const extra: Record<string, number | bigint> = {};
  const flagFields: [number, string, string][] = [
    [0x000001, 'u64', 'base_data_offset'],
    [0x000002, 'u32', 'sample_description_index'],
    [0x000008, 'u32', 'default_sample_duration'],
    [0x000010, 'u32', 'default_sample_size'],
    [0x000020, 'u32', 'default_sample_flags'],
  ];

  /*
    There are more flags than this, but for the limited diagnostics that we
    need, we only care about flags that affect the layout of the box. I.e. only
    flags that turn on optional fields in the box structure.
  */
  for (const field of flagFields) {
    // eslint-disable-next-line no-bitwise
    if (fields.flags & field[0]) {
      const func = ((p as unknown) as Record<string, () => number | bigint>)[
        field[1]
      ];
      extra[field[2]] = func.call(p);
    }
  }

  return { ...fields, ...extra };
}

/**
 * A Track Fragment Decode Time box.
 *
 * For media without discontinuities, this box should contain the sum of all the
 * preceding media samples for this track.
 *
 * When we clip video to create subclips, we need to rewrite the
 * baseMediaDecodeTime to avoid introducing discontinuities.
 */
interface TfdtBox {
  size: number;
  type: string;
  version: number;
  flags: number;
  baseMediaDecodeTime: bigint;
}

/**
 * Parse a Track Fragment decode Time box.
 */
export function parseTfdt(ab: ArrayBuffer) {
  const p = new BoxParser(new DataView(ab));
  const fields = {
    size: p.u32(),
    type: p.ascii(4),
    version: p.u8(),
    flags: p.u24(),
  };

  invariant(p.pos == 12, 'Expected offset 12');
  const u32v64 = fields.version == 0 ? p.u32.bind(p) : p.u64.bind(p); // 12
  return {
    ...fields,
    baseMediaDecodeTime: BigInt(u32v64()),
  };
}

/**
 * Read and parse a Track Fragment decode Time box.
 */
export async function readTfdt(blob: BlobView): Promise<TfdtBox> {
  const ab = await blob.arrayBuffer();
  return parseTfdt(ab);
}

/**
 * Parse a Movie Fragment Header Box.
 *
 * The sequenceNumber of a sequence of Movie Fragment Header Boxes in a ISOBMFF
 * is a strictly increasing counter that is used to validate that media has not
 * accidentally been reordered.
 */
function parseMfhd(ab: ArrayBuffer) {
  const p = new BoxParser(new DataView(ab));
  return {
    size: p.u32(),
    type: p.ascii(4),
    version: p.u8(),
    flags: p.u24(),
    sequenceNumber: p.u32(), // Offset 12
  };
}

/**
 * Read and parse a Movie Fragment Header Box.
 */
export async function readMfhd(blob: BlobView) {
  const ab = await blob.arrayBuffer();
  return parseMfhd(ab);
}

function decodeLanguage(bits: number) {
  // eslint-disable-next-line no-bitwise
  return [...Array(3)].map((e, i) => String.fromCharCode(0x60 + ((bits >> ((2 - i) * 5)) & 0x1f))).join('');
}

function parseMdhd(ab: ArrayBuffer) {
  const p = new BoxParser(new DataView(ab));
  return {
    size: p.u32(),
    type: p.ascii(4),
    version: p.u8(),
    flags: p.u24(),
    creationTime: p.u32(),
    modificationTime: p.u32(),
    timescale: p.u32(),
    duration: p.u32(),
    languages: decodeLanguage(p.u16()),
    predefined: p.u16()
  };
}

export async function readMdhd(blob: BlobView) {
  const ab = await blob.arrayBuffer();
  return parseMdhd(ab);
}

function parseTkhd(ab: ArrayBuffer) {
  const p = new BoxParser(new DataView(ab));
  return {
    size: p.u32(),
    type: p.ascii(4),
    version: p.u8(),
    flags: p.u24(),
    creationTime: p.u32(),
    modificationTime: p.u32(),
    track_ID: p.u32(),
    reserved1: p.u32(),
    duration: p.u32(),
    reserved2: [p.u32(), p.u32()],
    layer: p.u16(),
    alternateGroup: p.u16(),
    volume: p.u16(),
    reserved3: p.u16(),
    matrix: [p.u32(), p.u32(), p.u32(), p.u32(), p.u32(), p.u32(), p.u32(), p.u32(), p.u32()],
    width: p.u32(),
    height: p.u32()
  };
}

export async function readTkhd(blob: BlobView) {
  const ab = await blob.arrayBuffer();
  return parseTkhd(ab);
}

type BoxReader = (blob: BlobView) => Promise<Record<string, any>>;

export const boxReaders: Record<string, BoxReader> = {
  tfhd: readTfhd,
  tfdt: readTfdt,
  mfhd: readMfhd,
  mdhd: readMdhd,
  tkhd: readTkhd
};

export async function getBaseMediaDecodeTimeForMoof(
  moofBox: Box
): Promise<bigint | null> {
  invariant(moofBox.type == 'moof', 'Not a moof box');

  for await (const moofChild of childBoxes(moofBox)) {
    if (moofChild.type == 'traf') {
      for await (const trafChild of childBoxes(moofChild)) {
        if (trafChild.type == 'tfdt') {
          const tfdt = await readTfdt(trafChild.blob);
          return tfdt.baseMediaDecodeTime;
        }
      }
    }
  }
  return null;
}

export async function findVideoTrackTimescale(inBlob: BlobView): Promise<{ trackId: number, timescale: number } | null> {
  for await (const box of splitBoxes(inBlob)) {
    if (box.type != 'moov') {
      continue;
    }
    trakLoop: for await (const moovChild of childBoxes(box)) {
      if (moovChild.type != 'trak') {
        continue;
      }
      let trackId: number | undefined;
      for await (const trakChild of childBoxes(moovChild)) {
        if (trakChild.type == 'tkhd') {
          const tkhd = await readTkhd(trakChild.blob);
          if (tkhd.width == 0 || tkhd.height == 0) {
            continue trakLoop;
          }
          trackId = tkhd.track_ID;
        }

        if (trakChild.type != 'mdia') {
          continue;
        }

        for await (const mdiaChild of childBoxes(trakChild)) {
          if (mdiaChild.type != 'mdhd') {
            continue;
          }
          const mdhd = await readMdhd(mdiaChild.blob);
          const timescale = mdhd.timescale;
          if (trackId != null) {
            return { trackId, timescale };
          }
        }
      }
    }
  }
  return null;
}

export async function findTrackBaseMediaDecodeTime(inBlob: BlobView, trackId: number): Promise<bigint | null> {
  for await (const box of splitBoxes(inBlob)) {
    if (box.type != 'moof') {
      continue;
    }

    moofLoop: for await (const moofChild of childBoxes(box)) {
      if (moofChild.type != 'traf') {
        continue;
      }

      for await (const trafChild of childBoxes(moofChild)) {
        if (trafChild.type == 'tfhd') {
          const tkhd = await readTfhd(trafChild.blob);
          if (trackId != tkhd.track_ID) {
            continue moofLoop;
          }
        }

        if (trafChild.type != 'tfdt') {
          continue;
        }

        const tfdt = await readTfdt(trafChild.blob);
        return tfdt.baseMediaDecodeTime;
      }
    }
  }
  return null;
}
